import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { X, CornerDownRight } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { buildForest, flattenVisible, toggle, excludeArchived, isWithin } from "./facility-tree.js";
import { FacilityTree } from "./FacilityTree.js";
import { useMoveFacility, moveErrorCopy } from "./useMoveFacility.js";

/** No-op / empty fillers for FacilityTree's browse-only props — picker mode ignores them, so the drawer supplies
 *  harmless placeholders rather than loosening the (browse-strict) tree API. */
const NOOP = () => {};
const EMPTY_SET: ReadonlySet<number> = new Set();
const EMPTY_DELAYS: ReadonlyMap<number, number> = new Map();

export interface MoveFacilityDrawerProps {
  /** The facility being moved. Mounted only while a move is in progress (source != null ⇒ open). */
  source: FacilityNode;
  /** The caller's FULL permitted tree (incl. archived). Destination rows are built from the archived-EXCLUDED view of
   *  it (no archived targets), but `subCount` uses the full tree — the server moves the whole `path <@` subtree, so the
   *  eyebrow must count archived descendants too (panel). */
  flat: readonly FacilityNode[];
  /** The caller's bypass tier, computed by the parent from the FULL tree — drives the "{t("facilityManagement.move.topLevel")} (root)" option +
   *  target validity. Passed in (not recomputed from the filtered list) so it matches the source view (panel). */
  bypass: boolean;
  session: AuthSession | null;
  onClose: () => void;
  /** Announce a successful move to the parent's aria-live region. `newParentId` (null = root) lets a caller navigate to
   *  the destination — the drill-down "follows" a moved CURRENT facility; classic ignores it. */
  onMoved?: (sourceName: string, destName: string, newParentId: number | null) => void;
}

/**
 * The move destination picker (facility-features.md Move rework, RED-ZONE endpoint). A right slide-over that renders
 * the WHOLE tree in `FacilityTree` **picker mode** (no star/kebab/"+"/drag): the moving subtree is greyed + badged
 * MOVING, invalid targets are disabled, and a valid target shows an inline **"↳ Move here"** — the ONLY commit (a
 * whole-row click never moves; ADR-006 panel round-2 #1). Bypass tier also gets a **"{t("facilityManagement.move.topLevel")} (root)"** option.
 * Holds its OWN expand state (opens with the source's ancestors expanded so its current home is visible). Errors map
 * to inline copy via `useMoveFacility`; success closes + lets the tree query refetch. No footer button.
 */
export function MoveFacilityDrawer({ source, flat, bypass, session, onClose, onMoved }: MoveFacilityDrawerProps) {
  const { t } = useTranslation();
  const move = useMoveFacility(session);
  // Destination rows come from the ACTIVE (archived-excluded, hoisted) tree — mirrors the main view and never offers an
  // archived facility as a target. `subCount` + `bypass` use the FULL tree/parent value (panel — see prop docs).
  const pickerFlat = useMemo(() => excludeArchived(flat), [flat]);
  const forest = useMemo(() => buildForest(pickerFlat), [pickerFlat]);
  // Own expand state — start with the source's ANCESTORS expanded (open the tree to where it currently lives). isWithin
  // (source.path, f.path) is true for the source + its ancestors; drop the source itself.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(
    () => new Set(pickerFlat.filter((f) => f.path !== source.path && isWithin(source.path, f.path)).map((f) => f.id)),
  );
  const rows = useMemo(() => flattenVisible(forest, { isExpanded: (id) => expanded.has(id) }), [forest, expanded]);
  const onToggleExpand = (id: number) => setExpanded((s) => toggle(s, id));

  // How many facilities travel with the source (its descendants) — shown in the eyebrow.
  const subCount = useMemo(() => flat.filter((f) => isWithin(f.path, source.path)).length - 1, [flat, source.path]);

  const close = () => {
    move.reset();
    onClose();
  };
  const commit = (newParentId: number | null, destName: string) => {
    if (move.isPending) return; // one move at a time (a second pick before the first resolves would double-submit)
    move.mutate(
      { id: source.id, newParentId },
      {
        onSuccess: () => {
          onMoved?.(source.name, destName, newParentId);
          close();
        },
      },
    );
  };

  return (
    <Drawer isOpen isDismissable={!move.isPending} onOpenChange={(open) => !open && close()} className="gap-3">
      <DrawerHeader>
        <div className="min-w-0">
          <p className="text-muted-foreground text-[0.7rem] font-medium tracking-wide uppercase">
            {t("facilityManagement.move.movingTitle")}
            {subCount > 0 ? t("facilityManagement.move.subsMoveWith", { count: subCount }) : ""}
          </p>
          <DrawerTitle className="truncate">{source.name}</DrawerTitle>
        </div>
        <Button variant="ghost" size="icon" aria-label={t("facilityManagement.actions.close")} onPress={close} isDisabled={move.isPending}>
          <X className="size-4" />
        </Button>
      </DrawerHeader>
      <p className="text-muted-foreground text-sm">{t("facilityManagement.move.help")}</p>

      {move.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {moveErrorCopy(move.error, t)}
        </p>
      ) : null}

      <div className="-mx-2 flex-1 overflow-y-auto px-2">
        {/* {t("facilityManagement.move.topLevel")} (root) — bypass tier only (the server enforces); mirrors the picker rows' "↳ Move here". */}
        {bypass ? (
          <button
            type="button"
            disabled={move.isPending}
            onClick={() => commit(null, "the top level")}
            className={cn(
              "border-border hover:bg-muted focus-visible:ring-ring/50 my-1 flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm outline-none focus-visible:ring-3 disabled:opacity-50",
            )}
          >
            <CornerDownRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
            <span className="font-medium">{t("facilityManagement.move.topLevel")}</span>
            <span className="text-muted-foreground">{t("facilityManagement.move.noParent")}</span>
          </button>
        ) : null}

        <FacilityTree
          rows={rows}
          bypass={bypass}
          onToggleExpand={onToggleExpand}
          moveSource={source}
          onPickDestination={(node) => commit(node.id, node.name)}
          ariaLabel={t("facilityManagement.move.destination")}
          revealDelays={EMPTY_DELAYS}
          onRevealedRowFocus={NOOP}
          // Browse-only props — unused in picker mode.
          matchIds={EMPTY_SET}
          favorites={EMPTY_SET}
          selectedId={null}
          movableIds={EMPTY_SET}
          canAddIds={EMPTY_SET}
          manageSubtreeIds={EMPTY_SET}
          canDuplicateIds={EMPTY_SET}
          canInsertAboveIds={EMPTY_SET}
          archivedRootIds={EMPTY_SET}
          archivedMode={false}
          renamingId={null}
          editName=""
          savePending={false}
          onEditNameChange={NOOP}
          onToggleFavorite={NOOP}
          onSelect={NOOP}
          onMoveTo={NOOP}
          onAddChild={NOOP}
          onManage={NOOP}
          onArchive={NOOP}
          onDuplicate={NOOP}
          onInsertAbove={NOOP}
          onDelete={NOOP}
          onUnarchive={NOOP}
          onStartRename={NOOP}
          onSaveRename={NOOP}
          onCancelRename={NOOP}
          createUnder={null}
          session={session}
          onCreated={NOOP}
          onCancelCreate={NOOP}
        />
      </div>
    </Drawer>
  );
}
